import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Table from '@mui/material/Table'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import Fade from '@mui/material/Fade'
import TablePanel from '../Support/TablePanel'
import store from '../../redux/store'
import { useReorderValueMessagesMutation } from '../ValueNarrativesView/queries/useReorderValueMessagesMutation'
import Grid from '@mui/material/Grid'
import { connect, useSelector } from 'react-redux'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import IconButton from '@mui/material/IconButton'
import { ReactComponent as AddCircleIcon } from '../../assets/images/add-circle.svg'
import { ReactComponent as GreenCircleIcon } from '../../assets/images/green_circle.svg'
import { ReactComponent as OrangeTriangleIcon } from '../../assets/images/orange_triangle.svg'
import { ReactComponent as RedSquareIcon } from '../../assets/images/red_square.svg'
import { setStrategy, setValueMessageTableNumberOfItems } from '../../redux/actions'
import Typography from '@mui/material/Typography'
import { EnhancedTableHead, getComparator, stableSort, timeout } from '../../utilities/helpers'
import { GlobalStyles, SvgIcon } from '@mui/material'
import { EmptyPlaceholder } from '../../shared/components/EmptyPlaceholder'
import { arrayMove } from '@dnd-kit/sortable'
import { SortableTable } from '../../shared/components/SortableTable'
import Box from '@mui/material/Box'
import { useLocation } from 'react-router-dom'
import Tooltip from '@mui/material/Tooltip'
import ValueMessageExpandableRow from './components/ValueMessageExpandableRow'
import useZoomLevel from '../../shared/hooks/useZoomLevel'
import useValueMessageDisplayName from '../../shared/hooks/useDisplayNames/variants/useValueMessageDisplayName'
import useEventLogger from '../../shared/hooks/useEventLogger'
import { useIsRoadmapReadonly } from '../../shared/hooks/useIsRoadmapReadonly'
import { useIsSnapshotMode } from '../../shared/hooks/useIsSnapshotMode'
import useActivitiesDisplayName from '../../shared/hooks/useDisplayNames/variants/useActivitiesDisplayName'
import useOngoingDisplayName from '../../shared/hooks/useDisplayNames/variants/useOngoingDisplayName'
import { useIsDarkMode } from '../../shared/hooks/useIsDarkMode'
import Checkbox from '../../shared/components/Checkbox/Checkbox'
import ValueMessageActions from './components/ValueMessageActions/ValueMessageActions'
import Button from '@mui/material/Button'

function createData(
    value,
    status,
    supportingEvidence,
    evidenceGaps,
    ongoingActivities,
    id,
    order,
    panel,
    isCountrySpecific
) {
    return { value, status, supportingEvidence, evidenceGaps, ongoingActivities, id, order, panel, isCountrySpecific }
}

function mapDispatchToProps(dispatch) {
    return {
        setStrategy: (strat) => dispatch(setStrategy(strat)),
        setVMTableNumberOfRows: (num) => dispatch(setValueMessageTableNumberOfItems(num)),
    }
}

function ConnectedVMTable(props) {
    const { mutateAsync: reorderValueMessages } = useReorderValueMessagesMutation()
    const { logEvent, ACTIONS } = useEventLogger()
    const [page, setPage] = React.useState(0)
    const [dataRows, setDataRows] = useState([])
    const [loaded, setLoaded] = useState(false)
    const [vmData, setVMData] = useState({})
    const [lastAddedId, setLastAddedId] = useState(null)
    const isDarkMode = useIsDarkMode()
    const [updateTableFromPanel, setUpdateTableFromPanel] = useState(false)
    const [navigationStack, setNavigationStack] = useState([])
    const [selectedIds, setSelectedIds] = useState([])
    const [isTransferSelectionActive, setIsTransferSelectionActive] = useState(false)
    const [isReorderModeActive, setIsReorderModeActive] = useState(false)
    const dataRowsBeforeReorderRef = useRef(null)

    const isReadonlyMode = useIsRoadmapReadonly()
    const isSnapshotMode = useIsSnapshotMode()
    const activitiesDisplayName = useActivitiesDisplayName()
    const ongoingDisplayName = useOngoingDisplayName()

    const zoomLevel = useZoomLevel()

    // Columns declared as const weren't loading Store in time for render.
    // eslint-disable-next-line no-unused-vars

    const [columns, setColumns] = useState([
        { id: 'check', label: '', align: '', headPad: '' },
        { id: 'index', label: '', align: '', headPad: '' },
        { id: 'value', label: ' ', align: 'left', headPad: 2.5 },
        { id: 'status', label: 'Status', align: 'center', headPad: 2.5 },
        {
            id: 'supportingEvidence',
            label:
                (store.getState().settings?.displayNames?.supportingEvidence &&
                    store.getState().settings?.displayNames?.supportingEvidence) ||
                'Supporting Evidence',
            align: 'center',
            headPad: 2.5,
        },
        {
            id: 'evidenceGaps',
            label:
                (store.getState().settings?.displayNames?.evidenceGaps &&
                    store.getState().settings?.displayNames?.evidenceGaps) ||
                'Evidence Gaps',
            align: 'center',
            headPad: 2.5,
        },
        {
            id: 'ongoingActivities',
            label: `${ongoingDisplayName.value} ${activitiesDisplayName.plural}`,
            align: 'center',
            headPad: 2.5,
        },
        { id: 'panel', label: '', align: '', headPad: 2.5 },
    ])

    const [order, setOrder] = useState('asc')
    const [orderBy, setOrderBy] = useState('')

    const itemsToDisplay = useSelector((state) => state.valueMessagesTableNumberOfItems || 10)

    const handleSortDataRows = (sortingEvent) => {
        const { active, over } = sortingEvent

        if (!over || active.id === over.id) {
            return
        }

        const indexOfDestinationRow = sortingEvent.over.data.current.sortable.index
        const indexOfSourceRow = sortingEvent.active.data.current.sortable.index
        const newDataRows = arrayMove(dataRows, indexOfSourceRow, indexOfDestinationRow)
        setDataRows(newDataRows)

        setOrderBy('index')
    }

    const valueMessageDisplayName = useValueMessageDisplayName()

    async function getVMData() {
        createDataRows(props.valueMessages)

        setLoaded(true)
        let vms = {}
        for (let vm of props.valueMessages) {
            vms[vm._id] = vm
        }
        setVMData(vms)
    }

    useEffect(() => {
        if (updateTableFromPanel) {
            setUpdateTableFromPanel(false)
            props.onUpdate()
        }

        getVMData()
    }, [updateTableFromPanel, props.valueMessages])

    // Highlight and page navigation logic for last added row
    useEffect(() => {
        if (!lastAddedId || !dataRows.length) return

        const newIndex = dataRows.findIndex((row) => row.id === lastAddedId)
        if (newIndex === -1) return

        const newPage = Math.floor(newIndex / itemsToDisplay)
        const positionOnPage = newIndex % itemsToDisplay
        if (newPage !== page) setPage(newPage)

        const scrollToBottom = () => {
            window.scrollTo({
                top: document.body.scrollHeight,
                behavior: 'smooth',
            })
        }

        setTimeout(() => {
            if (positionOnPage > itemsToDisplay / 2) {
                scrollToBottom()
            }
        }, 300)

        let timeoutId = setTimeout(() => setLastAddedId(null), 1500)

        return () => clearTimeout(timeoutId)
    }, [dataRows, lastAddedId, itemsToDisplay, page])

    // Page correction after deletion
    useEffect(() => {
        if (!dataRows.length) return

        const maxPage = Math.max(0, Math.ceil(dataRows.length / itemsToDisplay) - 1)

        if (page > maxPage) {
            setPage(maxPage)
            setLoaded(false)
            setTimeout(() => setLoaded(true), 0)
        }
    }, [dataRows, page, itemsToDisplay])

    useEffect(() => {
        if (!props.newValueMessageId) return
        setLastAddedId(props.newValueMessageId)
    }, [props.newValueMessageId])

    function createDataRows(data) {
        let arr = []

        data.forEach((vm) => {
            let dataRow = createData(
                // vm.priority,
                vm.name,
                vm.status,
                vm.supportingEvidenceCount ? vm.supportingEvidenceCount : 0,
                vm.evidenceGaps.length,
                vm.ongoingActivities.length,
                vm._id,
                vm.order,
                '',
                vm.isCountrySpecific ? vm.isCountrySpecific : false
            )
            arr.push(dataRow)
        })
        arr.sort((a, b) => a.order - b.order)
        setDataRows(arr)
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        props.setVMTableNumberOfRows(+event.target.value)
        setPage(0)
    }

    const handleRequestSort = (ent, property) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const location = useLocation()

    const paramsCategoryId = location.pathname.split('/')[3]
    const paramsValueNarrativeId = location.pathname.split('/')[5]
    const paramsValueMessageId = location.pathname.split('/')[7]

    const valueMessageForPanel = useMemo(() => {
        return vmData[paramsValueMessageId] || {}
    }, [paramsValueMessageId, vmData])

    useEffect(() => {
        if (valueMessageForPanel?.id) {
            logEvent(ACTIONS.VIEW, { type: 'Value Message', _id: valueMessageForPanel?.id })
        }
    }, [valueMessageForPanel?.id])

    const handleSelectValueMessage = (messageId) => {
        const message = vmData[messageId]

        if (message && message.categoryId && message.narrativeId && message._id) {
            props.handleSelectMessage(message.categoryId, message.narrativeId, message._id)
        }
    }

    useEffect(() => {
        setNavigationStack(location?.autoOpenData?.previousDataStack ? location.autoOpenData.previousDataStack : [])
    }, [location?.autoOpenData?.previousDataStack])

    function handlePanelClose() {
        props.handleSelectNarrative(paramsCategoryId, paramsValueNarrativeId)
        setNavigationStack([])
        location.autoOpenData = null
    }

    const globalStyles = (
        <GlobalStyles
            styles={{
                '.MuiTableCell-root': {
                    borderBottom: 'none !important',
                },
                '.highlight-row': {
                    backgroundColor: `${isDarkMode ? 'rgba(81, 179, 255, 0.25)' : 'rgba(81, 179, 255, 0.15)'} !important`,
                    transition: 'background-color 1.5s ease',
                },
                tr: {
                    transition: 'background-color 1.5s ease',
                },
            }}
        />
    )

    const sortedDataRows = stableSort(dataRows, getComparator(order, orderBy)).slice(
        page * itemsToDisplay,
        page * itemsToDisplay + itemsToDisplay
    )
    const isValueMessagesDataEmpty = !sortedDataRows?.length

    const [expandedRow, setExpandedRow] = useState({})

    const isChecked = useCallback(
        (valueMessageId) => {
            return selectedIds.some((item) => item._id === valueMessageId)
        },
        [selectedIds]
    )

    const handleCheckValueMessage = (valueMessageId) => {
        const valueMessage = props.valueMessages.find((item) => item._id === valueMessageId)

        if (selectedIds.some((item) => item._id === valueMessage._id)) {
            setSelectedIds((prev) => prev.filter((item) => item._id !== valueMessageId))
        } else {
            setSelectedIds((prev) => {
                return [...prev, valueMessage]
            })
        }
    }

    const handleSelectAllValueMessages = () => {
        setSelectedIds((prev) => (prev.length ? [] : props.valueMessages))
    }

    const handleResetActions = () => {
        if (dataRowsBeforeReorderRef.current) {
            setDataRows(dataRowsBeforeReorderRef.current)
            dataRowsBeforeReorderRef.current = null
        }
        setSelectedIds([])
        setIsTransferSelectionActive(false)
        setIsReorderModeActive(false)
    }

    const handleEnterReorderMode = () => {
        dataRowsBeforeReorderRef.current = dataRows
        setOrderBy('')
        setOrder('asc')
        setPage(0)
        setIsReorderModeActive(true)
    }

    const handleSaveReorder = async () => {
        const ids = dataRows.map((rowData) => rowData.id)
        await reorderValueMessages(ids)
        dataRowsBeforeReorderRef.current = null
        setIsReorderModeActive(false)
    }

    useEffect(() => {
        setExpandedRow({})
    }, [props.selectedNarrative])

    return (
        <>
            {globalStyles}
            <Fade in={loaded} timeout={800}>
                <Grid container item xs>
                    <Box display="flex" gap={1} alignItems="center" py={1} width="100%">
                        <Typography color="figma.grayscale.textHier1" variant="h4" fontWeight={700}>
                            {valueMessageDisplayName.plural}
                        </Typography>
                        {!isReadonlyMode && !isSnapshotMode && (
                            <IconButton onClick={props.handleCreateButtonClick} sx={{ p: 0.5 }}>
                                <SvgIcon component={AddCircleIcon} color="primary" inheritViewBox fontSize="medium" />
                            </IconButton>
                        )}

                        <Box ml={-0.5}>
                            <ValueMessageActions
                                valueMessages={selectedIds}
                                isReorderModeActive={isReorderModeActive}
                                hasReorderChanges={dataRows !== dataRowsBeforeReorderRef.current}
                                onEnterReorderMode={handleEnterReorderMode}
                                onSaveReorder={handleSaveReorder}
                                isTransferSelectionActive={isTransferSelectionActive}
                                onEnterTransferSelection={() => setIsTransferSelectionActive(true)}
                                onResetActions={handleResetActions}
                            />
                        </Box>
                    </Box>
                    <Box>
                        <Button
                            sx={{
                                padding: 0,
                                fontSize: 15,
                                visibility: isTransferSelectionActive ? 'visible' : 'hidden',
                            }}
                            onClick={() => handleSelectAllValueMessages()}
                        >
                            {selectedIds.length ? 'Deselect All' : 'Select All'}
                        </Button>
                    </Box>
                    {isValueMessagesDataEmpty && (
                        <EmptyPlaceholder
                            title={`No ${valueMessageDisplayName.singular} to display`}
                            subtitle={`Click 'Add New' on the left panel to create a new ${valueMessageDisplayName.singular}`}
                        />
                    )}
                    {!isValueMessagesDataEmpty && (
                        <>
                            <TableContainer>
                                <Table stickyHeader aria-label="sticky table" size="small">
                                    <EnhancedTableHead
                                        columns={columns}
                                        order={order}
                                        orderBy={orderBy}
                                        align={columns.align}
                                        onRequestSort={handleRequestSort}
                                        disabled={isReorderModeActive}
                                    />
                                    <SortableTable.Body
                                        data={sortedDataRows}
                                        onSort={handleSortDataRows}
                                        disabled={!isReorderModeActive}
                                    >
                                        {({ rowData, index, rowProps }) => {
                                            const isExpanded = !!expandedRow[rowData?.id]
                                            const valueMessageId = rowData.id

                                            return (
                                                <>
                                                    <SortableTable.Row
                                                        {...rowProps}
                                                        className={rowData.id === lastAddedId ? 'highlight-row' : ''}
                                                    >
                                                        {columns.map((column) => {
                                                            const value = rowData[column.id]
                                                            if (column.id === 'status') {
                                                                return (
                                                                    <TableCell
                                                                        key={column.id}
                                                                        align={column.align}
                                                                        sx={{ border: 'none' }}
                                                                    >
                                                                        <Tooltip title={value}>
                                                                            <Box>
                                                                                {value === 'Supported' && (
                                                                                    <SvgIcon
                                                                                        fontSize="small"
                                                                                        component={GreenCircleIcon}
                                                                                        inheritViewBox
                                                                                    />
                                                                                )}
                                                                                {value === 'Supported-suboptimal' && (
                                                                                    <SvgIcon
                                                                                        fontSize="small"
                                                                                        component={OrangeTriangleIcon}
                                                                                        inheritViewBox
                                                                                    />
                                                                                )}
                                                                                {value === 'Aspirational' && (
                                                                                    <SvgIcon
                                                                                        fontSize="small"
                                                                                        component={RedSquareIcon}
                                                                                        inheritViewBox
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                        </Tooltip>
                                                                    </TableCell>
                                                                )
                                                            } else if (column.id === 'index') {
                                                                return (
                                                                    <TableCell
                                                                        hover
                                                                        key={column.id}
                                                                        align={column.align}
                                                                        sx={{
                                                                            border: 'none',
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body9"
                                                                            sx={{ color: '#808080' }}
                                                                        >
                                                                            {index + page * itemsToDisplay + 1}.
                                                                        </Typography>
                                                                    </TableCell>
                                                                )
                                                            } else if (column.id === 'check') {
                                                                return (
                                                                    <TableCell
                                                                        hover
                                                                        key={column.id}
                                                                        align={column.align}
                                                                        sx={{
                                                                            border: 'none',
                                                                            borderTopLeftRadius: index === 0 ? 14 : 0,
                                                                            borderBottomLeftRadius:
                                                                                index ===
                                                                                    dataRows.slice(
                                                                                        page * itemsToDisplay,
                                                                                        (page + 1) * itemsToDisplay
                                                                                    ).length -
                                                                                        1 && !isExpanded
                                                                                    ? 14
                                                                                    : 0,
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body9"
                                                                            sx={{ color: '#808080' }}
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    padding: 12,
                                                                                    maxWidth: 4,
                                                                                }}
                                                                            >
                                                                                {isTransferSelectionActive && (
                                                                                    <Checkbox
                                                                                        checked={isChecked(
                                                                                            valueMessageId
                                                                                        )}
                                                                                        onCheck={() =>
                                                                                            handleCheckValueMessage(
                                                                                                valueMessageId
                                                                                            )
                                                                                        }
                                                                                        label={''}
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        </Typography>
                                                                    </TableCell>
                                                                )
                                                            } else if (
                                                                column.id === 'value' &&
                                                                rowData['isCountrySpecific']
                                                            ) {
                                                                return (
                                                                    <TableCell
                                                                        key={column.id}
                                                                        align={column.align}
                                                                        sx={{ border: 'none' }}
                                                                    >
                                                                        <Typography variant="body1">
                                                                            {column.format && typeof value === 'number'
                                                                                ? column.format(value)
                                                                                : value}
                                                                        </Typography>
                                                                    </TableCell>
                                                                )
                                                            } else if (column.id === 'panel') {
                                                                return (
                                                                    <TableCell
                                                                        key={column.id}
                                                                        align={column.align}
                                                                        sx={{
                                                                            border: 'none',
                                                                            borderTopRightRadius: index === 0 ? 14 : 0,
                                                                            borderBottomRightRadius:
                                                                                index ===
                                                                                    dataRows.slice(
                                                                                        page * itemsToDisplay,
                                                                                        (page + 1) * itemsToDisplay
                                                                                    ).length -
                                                                                        1 && !isExpanded
                                                                                    ? 14
                                                                                    : 0,
                                                                            cursor: 'pointer',
                                                                        }}
                                                                        onClick={() =>
                                                                            handleSelectValueMessage(rowData.id)
                                                                        }
                                                                    >
                                                                        <IconButton
                                                                            sx={(theme) => ({
                                                                                padding: theme.spacing(1.5),
                                                                                marginInline: theme.spacing(-0.75),
                                                                            })}
                                                                        >
                                                                            <ArrowForwardIosIcon color="primary" />
                                                                        </IconButton>
                                                                    </TableCell>
                                                                )
                                                            } else if (
                                                                [
                                                                    'supportingEvidence',
                                                                    'ongoingActivities',
                                                                    'evidenceGaps',
                                                                ].includes(column.id)
                                                            ) {
                                                                const isExpanded =
                                                                    expandedRow[rowData?.id] === column.id

                                                                return (
                                                                    <TableCell
                                                                        key={column.id}
                                                                        align={column.align}
                                                                        sx={{ border: 'none' }}
                                                                    >
                                                                        <IconButton
                                                                            sx={(theme) => ({
                                                                                height: theme.spacing(5),
                                                                                width: theme.spacing(5),
                                                                                backgroundColor: isExpanded
                                                                                    ? `${theme.palette.figma.primary2} !important`
                                                                                    : 'unset',
                                                                            })}
                                                                            onClick={() => {
                                                                                isExpanded
                                                                                    ? setExpandedRow({
                                                                                          ...expandedRow,
                                                                                          [rowData.id]: null,
                                                                                      })
                                                                                    : setExpandedRow({
                                                                                          ...expandedRow,
                                                                                          [rowData.id]: column.id,
                                                                                      })
                                                                            }}
                                                                        >
                                                                            <Typography variant="h4" color="primary">
                                                                                {value}
                                                                            </Typography>
                                                                        </IconButton>
                                                                    </TableCell>
                                                                )
                                                            } else {
                                                                const isNumber =
                                                                    column.format && typeof value === 'number'
                                                                return (
                                                                    <TableCell
                                                                        key={column.id}
                                                                        align={column.align}
                                                                        sx={{
                                                                            border: 'none',
                                                                            minWidth: !isNumber ? '600px' : 'unset',
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant={
                                                                                column.format &&
                                                                                typeof value === 'number'
                                                                                    ? 'h4'
                                                                                    : '16x500'
                                                                            }
                                                                            color={
                                                                                column.format &&
                                                                                typeof value === 'number'
                                                                                    ? 'primary'
                                                                                    : ''
                                                                            }
                                                                            sx={(theme) => {
                                                                                if (!isNumber) {
                                                                                    return {
                                                                                        fontSize: `${Math.min(
                                                                                            Math.max(
                                                                                                16,
                                                                                                zoomLevel * 16
                                                                                            ),
                                                                                            40
                                                                                        )}px !important`,
                                                                                    }
                                                                                }
                                                                            }}
                                                                        >
                                                                            {column.format && typeof value === 'number'
                                                                                ? column.format(value)
                                                                                : value}
                                                                        </Typography>
                                                                    </TableCell>
                                                                )
                                                            }
                                                        })}
                                                    </SortableTable.Row>
                                                    <ValueMessageExpandableRow
                                                        isExpanded={isExpanded}
                                                        expandedRowType={expandedRow[rowData.id]}
                                                        valueMessageId={rowData.id}
                                                        colorBySubpopulation={props.colorBySubpopulation}
                                                        navigationStack={navigationStack}
                                                        valueMessage={vmData[rowData.id]}
                                                    />
                                                </>
                                            )
                                        }}
                                    </SortableTable.Body>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={dataRows.length}
                                rowsPerPage={itemsToDisplay}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </>
                    )}
                    {valueMessageForPanel?._id && (
                        <TablePanel
                            title={valueMessageForPanel.name}
                            data={{
                                ...valueMessageForPanel,
                                narrative: props.selectedNarrative,
                            }}
                            type={'valueMessage'}
                            closePanel={handlePanelClose}
                            openPanelAction={true}
                            updateParent={setUpdateTableFromPanel}
                            navigationStack={navigationStack}
                        />
                    )}
                </Grid>
            </Fade>
        </>
    )
}

const VMTable = connect(null, mapDispatchToProps)(ConnectedVMTable)

export default VMTable
